import State from "@/helpers/pattern/state";
import { SeOOB } from "@/models/operativos/seOOB";
import { modelIsModified } from "@/helpers/vue-mc";
import seOobAPI from "@/services/operativos/seOOB";
import CombosAPI from "@/services/catalogos";
import { EventBus } from "@/events/event-bus";
import * as events from "@/constants/event-names";
import { clearSwal } from "@/constants/sweet-alerts";
import { scrollToForm } from "@/helpers/form-scroll-to";
import { setSelectsInactiveData } from "@/helpers/form-common";
import StringMask from "string-mask";

/**
 * State Pattern for se form
 */
export default class SeOOBStates extends State {
  /**
   * General Methods
   */
  base() {
    return {
      clear({ $setState }) {
        $setState("initial");
      }
      , async askForClear({ $data }) {
        try {
          if ((await $data.$swal(clearSwal)).value) this.clear();
        } catch (error) {
          console.error(error);
        }
      }
      , resetModel({ $data }) {
        $data.seOOB = new SeOOB();
        $data.refreshkey += 1;
      }
      , resetValidators({ $data }) {
        $data.$v.$reset();
        $data.refreshkey += 1;
      }
      , isDisableButtonSubmit({ $data }) {
        return $data.hasErrors($data.$v.seOOB) || $data.view.action.submit.busy || !modelIsModified($data.seOOB);
      }
      , async loadSelectsData({ $data }) {
        const { data: { results: { combo: provinciaList } } } = await CombosAPI.get({ option: "provincia" });
        $data.view.elem.select.provincia.filteredList = provinciaList ? provinciaList.filter((value) => value.status) : [];
        $data.view.elem.select.provincia.completeList = provinciaList || [];
      }
      , async loadSelectLocalidad({ $data }, provincia = $data.seOOB.provincia) {
        if (provincia) {
          const { data: { results: { combo: localidadList } } } = await CombosAPI.getByParameter({ option: "LocalidadesByProvincia", parameter: provincia });
          $data.view.elem.select.localidad.filteredList = localidadList ? localidadList.filter((value) => value.status) : [];
          $data.view.elem.select.localidad.completeList = localidadList || [];
        }
      }
    };
  }

  /**
   * States and their methods
   */
  states() {
    return {
      initial: {
        $constructor: function ({ $data }) {
          // Reset Models and validators
          this.resetModel();
          this.loadSelectsData();
          this.resetValidators();

          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.save;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.save;
        }
        , async submit(
          { $data },
          {
            nombre, nombrePersonaContacto, cuit, direccion, altura, provincia, localidad, referencia, cp, email, telefono, latitud, longitud, geoRelevancia, photos, estatus 
          }
        ) {
          try {
            if (!$data.isAllValid($data.$v)) {
              EventBus.$emit(events.SHOW_TOASTER_MESSAGE, "error", "VL0000", $data.$t("errors.validof"));
              scrollToForm($data);
              return false;
            }

            $data.view.action.submit.busy = true;

            const response = await seOobAPI.save({
              clave: null
              , nombre
              , nombrePersonaContacto
              , cuit: cuit ? cuit.replace(/\D/g, "") : ""
              , direccion
              , altura
              , provincia: provincia ? Number(provincia) : null
              , localidad: localidad ? Number(localidad) : null
              , referencia
              , cp
              , email
              , telefono
              , latitud
              , longitud
              , geoRelevancia
              , photos: photos && photos.length > 0 ? photos.map((item) => item.join(":")).join(",") : null
              , estatus
            });

            $data.view.action.submit.busy = false;
            this.resetModel();
            this.resetValidators();
            $data.$refs.seOOBTable.refreshData();
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
      , editing: {
        $constructor: function (
          { $data },
          {
            clave, nombre, nombrePersonaContacto, cuit, direccion, altura, provincia, localidad, referencia, cp, email, telefono, latitud, longitud, geoRelevancia, photos, estatus 
          }
        ) {
          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.update;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.update;

          // Set options to combos if not are active
          setSelectsInactiveData(provincia, $data.view.elem.select.provincia.completeList, $data.view.elem.select.provincia.filteredList);
          setSelectsInactiveData(localidad, $data.view.elem.select.localidad.completeList, $data.view.elem.select.localidad.filteredList);

          $data.seOOB = new SeOOB({
            clave
            , nombre
            , nombrePersonaContacto
            , cuit: StringMask.apply(cuit, "##-########-#")
            , direccion
            , altura
            , provincia: provincia ? Number(provincia) : null
            , localidad: localidad ? Number(localidad) : null
            , referencia
            , cp
            , email
            , telefono
            , latitud
            , longitud
            , geoRelevancia
            , photos: photos ? photos.split(",").map((item) => item.split(":")) : null
            , estatus
          });

          this.resetValidators();
          scrollToForm($data);
        }
        , async submit(
          { $data, $setState },
          {
            clave, nombre, nombrePersonaContacto, cuit, direccion, altura, provincia, localidad, referencia, cp, email, telefono, latitud, longitud, geoRelevancia, photos, estatus 
          }
        ) {
          try {
            if (!$data.isAllValid($data.$v)) {
              EventBus.$emit(events.SHOW_TOASTER_MESSAGE, "error", "VL0000", $data.$t("errors.validof"));
              scrollToForm($data);
              return false;
            }
            $data.view.action.submit.busy = true;

            const response = await seOobAPI.update({
              clave
              , nombre
              , nombrePersonaContacto
              , cuit: cuit ? cuit.replace(/\D/g, "") : ""
              , direccion
              , altura
              , provincia: provincia ? Number(provincia) : null
              , localidad: localidad ? Number(localidad) : null
              , referencia
              , cp
              , email
              , telefono
              , latitud
              , longitud
              , geoRelevancia
              , photos: photos && photos.length > 0 ? photos.map((item) => item.join(":")).join(",") : null
              , estatus
            });

            $data.$refs.seOOBTable.refreshData();
            $data.view.action.submit.busy = false;
            $setState("initial");
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/views/operativos/seOOB/seOOB-state.js